import { OneOrMany } from 'util/domain';
import {
	isPurgedOrPurging,
	ShareBaseLibrary,
	ShareBaseFolder
} from 'phoenix/domain';
import { LibraryFactory } from 'sharebase/factories';
import { Library } from 'sharebase/domain';
import { ISortContentFunction } from 'sharebase/filters/sortContent';
import { 
	IFolderService, 
	LibraryService 
} from 'sharebase/services';
import { AclDatastore } from 'sharebase/dataStores/aclDatastore';
import { ToolbarService } from 'sharebase-app/services/appToolBarService';

/**
* @ngdoc service
* @name  sharebase-app.sbaLibraryDataStore
* 
* @description 
* A datastore used to interact with the user's Libraries
* 
**/
export class LibraryDataStore {
	public static $inject =  [
		'$q',
		'sbSortContentFilter',
		'sbLibraryFactory',
		'sbLibraryService',
		'sbFolderService',		
		'sbaToolbarService',
		'sbAclDatastore'
	];

	/**
	* The currently loaded library representation
	*/
	get library() { return this._library; }
	/**
	* The users personal library
	*/
	get myLibrary() { return this._myLibrary; }
	/**
	* The top level folders of the users personal library
	*/
	get folders() { return this._folders; }

	private _library:Library;
	private _myLibrary:Library;
	private _folders:ShareBaseFolder[] = [];

	constructor(
		private $q:ng.IQService, 
		private sortContent:ISortContentFunction, 
		private sbLibraryFactory:LibraryFactory, 
		private sbLibraryService:LibraryService, 
		private sbFolderService:IFolderService, 
		private sbaToolbarService:ToolbarService,
		private sbAclDatastore:AclDatastore
	){}

	public loadMyLibrary() {
		return this.sbLibraryService
			.getUserLibrary()
			.then(myLibrary => {
				this._myLibrary = this.sbLibraryFactory.create(myLibrary);
				this._library = this._myLibrary;
				return this.sbLibraryService.getLibraryFolders(this._myLibrary);
			})
			.then(folders => {
				this._folders = folders.filter((f) => !isPurgedOrPurging(f));
				this.sort(this.sbaToolbarService.sortType);

				return this._myLibrary;
			});
	}
	
	/*
	* @description
	* Loads or refreshes the users home library and its top level folders.
	*
	* @returns A promise that will resolve with the
	* users home library and its top level folders.
	*/
	public loadLibrary(libraryId?:number|string) {
		if(!libraryId){ 
			return this.loadMyLibrary(); 
		}

		if(typeof libraryId === 'string'){
			libraryId = parseInt(libraryId);
		}			

		return this.sbLibraryService
			.getLibraries()
			.then(libraries => {
				libraries = libraries.filter(l => l.LibraryId === libraryId);

				if(libraries.length === 0 ){
					throw new Error('Invalid library ID or user does not have rights.');
				} 
				this._library = this.sbLibraryFactory.create(libraries[0]);
				return this.sbLibraryService.getLibraryFolders(this._library);							
			})
			.then(folders => {
				this._folders = folders.filter(f => !isPurgedOrPurging(f));
			})
			.then(() => this._library);
	}

	public deleteFolder(deleted:ShareBaseFolder, purge:boolean = false) {
		return this.sbFolderService
			.deleteFolder(deleted, purge)
			.then(() => this.pruneFolder(deleted));
	}

	public moveFolder(source:OneOrMany<ShareBaseFolder>, destination:ShareBaseFolder) {
		if (!Array.isArray(source)) {
			source = [source];
		}

		return this.$q.all(source.map(src => {
			return this.sbFolderService
				.moveFolder(src, destination)
				.then(res => this.pruneFolder(src));
		}));
	}

	/*
	*
	* Create a sub folder
	* 
	* @param {object} name A name for the new folder
	*
	* @returns {Deferred} A promise that will resolve with a new folder
	*/
	public createFolder(name:string) {
		return this.sbLibraryService
			.createFolder(this._library.LibraryId, name)
			.then(newFolder => {
				this._folders.push(newFolder);
				return newFolder;
			});
	}

	public getCurrentUserAcl() {
		this.guardLibrary();
		return this.sbAclDatastore.load(this._library);
	}

	/**
	 * Sort library content based on provided sort parameters
	 * 
	 * @param {Object} sortType Parameters that determine how to sort the data
	 */

	public sort(sortType) {
		this._folders = this.sortContent(this._folders, sortType.type, sortType.ascending) as ShareBaseFolder[];
	}
	
	/** 
	* Renames a folder within the current library
	* 
	* @param {ShareBaseFolder} folder The folder object being renamed
	* @param {string} newName The new name for the folder
	* 
	*/
	public renameItem(folder:ShareBaseFolder, newName:string) {
		return this.sbFolderService
			.renameFolder(folder, newName)
			.then(renamedFolder => {
				const index = this._folders.indexOf(folder);
				if(index >= 0){
					this._folders[index] = renamedFolder;
				}

				return renamedFolder;
			});
	}

	public clearStore(){
		this._folders = [];
		this._myLibrary = this._library = null;
	}

	private guardLibrary() {
		if(!this._library){
			throw new Error('Library not loaded.');
		}
	}

	private pruneFolder(folder:ShareBaseFolder) {
		const index = this._folders.indexOf(folder);
		if(index >= 0){
			this._folders.splice(index, 1);
		}
	}
}


// WEBPACK FOOTER //
// ./src/sharebase-app/dataStores/libraryDataStore.ts